import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { okSuccessToast, apiErrorToast } from "../utils/ToastUtil";
import SmallLoader from "./loading-screen/SmallLoader";

const MerchantApproval = ({ row, refresh }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState("");
  const [remark, setRemark] = useState("");

  const handleOpen = () => {
    setOpen(true);

    setSelectedResponse("");
    setRemark("");
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedResponse("");
    setRemark("");
  };

  const handleResponse = async (response) => {
    if (!selectedResponse) {
      apiErrorToast("Please select a response");
      return;
    }

    setLoading(true);
    try {
      const data = {
        task_id: row.id,
        merchant_response: selectedResponse,
        remark: remark || "",
      };

      postJsonData(
        ApiEndpoints.MERCHANT_RESPONSE,
        data,
        setLoading,
        (res) => {
          okSuccessToast(`Task ${selectedResponse} successfully`);
          handleClose();
          refresh();
        },
        (error) => {
          apiErrorToast(error);
        }
      );
    } catch (error) {
      apiErrorToast("Failed to submit response");
    } finally {
      setLoading(false);
    }
  };

  // केवल merchant के लिए और केवल paid task के लिए दिखाएँ
  if (row.task_type !== "paid" || row.merchant_response === "accepted") {
    return null;
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "orange";
      case "accepted":
        return "green";
      case "rejected":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pending Approval";
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      default:
        return "No Response";
    }
  };

  return (
    <>
      <Tooltip title="Merchant Approval Required">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: row.merchant_response === "pending" ? "pointer" : "default",
          }}
          onClick={row.merchant_response === "pending" ? handleOpen : null}
        >
          <Box
            sx={{
              padding: "4px 12px",
              borderRadius: "20px",
              backgroundColor:
                getStatusColor(row.merchant_response || "pending") + "20",
              border: `1px solid ${getStatusColor(
                row.merchant_response || "pending"
              )}`,
              color: getStatusColor(row.merchant_response || "pending"),
              fontWeight: 500,
              fontSize: "12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover":
                row.merchant_response === "pending"
                  ? {
                      backgroundColor:
                        getStatusColor(row.merchant_response || "pending") +
                        "30",
                    }
                  : {},
            }}
          >
            {row.merchant_response === "accepted" && (
              <CheckCircleIcon fontSize="small" />
            )}
            {row.merchant_response === "rejected" && (
              <CancelIcon fontSize="small" />
            )}
            {getStatusText(row.merchant_response || "pending")}
          </Box>
        </Box>
      </Tooltip>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            Task Approval Required
          </Typography>
        </DialogTitle>
   <SmallLoader loading={loading} />
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary">
              This is a <strong>Paid Task</strong> requiring your approval
              before assignment.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Task Details:
            </Typography>
            <Box sx={{ mt: 1, pl: 2 }}>
              <Typography variant="body2">
                <strong>Task ID:</strong> {row.id}
              </Typography>
              <Typography variant="body2">
                <strong>Project:</strong> {row.project_name}
              </Typography>
              <Typography variant="body2">
                <strong>Category:</strong> {row.category}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> ₹{row.amount}
              </Typography>
              <Typography variant="body2">
                <strong>Priority:</strong> {row.priority}
              </Typography>
              <Typography variant="body2">
                <strong>Description:</strong> {row.description}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ fontWeight: "bold", mb: 2 }}>
                Select Your Response:
              </FormLabel>
              <RadioGroup
                value={selectedResponse}
                onChange={(e) => setSelectedResponse(e.target.value)}
                sx={{ gap: 1 }}
              >
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                  <FormControlLabel
                    value="accepted"
                    control={<Radio color="success" />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CheckCircleIcon color="success" />
                        <Typography fontWeight="medium">Accept Task</Typography>
                      </Box>
                    }
                    sx={{
                      flex: 1,
                      border:
                        selectedResponse === "accepted"
                          ? "2px solid #4caf50"
                          : "1px solid #ddd",
                      borderRadius: 1,
                      p: 1.5,
                      m: 0,
                    }}
                  />
                  <FormControlLabel
                    value="rejected"
                    control={<Radio color="error" />}
                    label={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <CancelIcon color="error" />
                        <Typography fontWeight="medium">Reject Task</Typography>
                      </Box>
                    }
                    sx={{
                      flex: 1,
                      border:
                        selectedResponse === "rejected"
                          ? "2px solid #f44336"
                          : "1px solid #ddd",
                      borderRadius: 1,
                      p: 1.5,
                      m: 0,
                    }}
                  />
                </Box>
              </RadioGroup>
            </FormControl>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Remarks (Optional)"
              placeholder="Add any remarks or comments..."
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              variant="outlined"
              helperText="This remark will be saved with your response"
            />
          </Box>

          {selectedResponse === "accepted" && (
            <Box
              sx={{ mb: 2, p: 2, bgcolor: "success.light", borderRadius: 1 }}
            >
              <Typography variant="body2" color="success.dark">
                <strong>Accepting this task means:</strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li>You agree to pay ₹{row.amount} for this task</li>
                  <li>
                    The task will be available for assignment to developers
                  </li>
                  <li>You will be notified when the task is completed</li>
                </ul>
              </Typography>
            </Box>
          )}

          {selectedResponse === "rejected" && (
            <Box sx={{ mb: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
              <Typography variant="body2" color="error.dark">
                <strong>Rejecting this task means:</strong>
                <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
                  <li>The task will not be assigned to any developer</li>
                  <li>
                    You may need to discuss with the admin about alternatives
                  </li>
                  <li>The task amount will not be charged</li>
                </ul>
              </Typography>
            </Box>
          )}

          <Box sx={{ p: 2, bgcolor: "warning.light", borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark">
              <strong>Note:</strong> Once submitted, your response cannot be
              changed. Please review the task details carefully before
              submitting.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button onClick={handleClose} disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={selectedResponse === "rejected" ? "error" : "success"}
            onClick={() => handleResponse(selectedResponse)}
            disabled={!selectedResponse || loading}
            startIcon={
              loading && <CircularProgress size={20} color="inherit" />
            }
            sx={{
              minWidth: "120px",
              backgroundColor:
                selectedResponse === "rejected" ? "#f44336" : "#4caf50",
              "&:hover": {
                backgroundColor:
                  selectedResponse === "rejected" ? "#d32f2f" : "#388e3c",
              },
            }}
          >
            {loading
              ? "Submitting..."
              : `Submit ${
                  selectedResponse === "rejected" ? "Rejection" : "Acceptance"
                }`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MerchantApproval;
